import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Loader2, Send, Radar, AlertTriangle, PhoneMissed, CalendarX, TrendingUp } from "lucide-react";
import { EASE, Reveal, SectionTag } from "./motion";
import { waLink } from "../lib/config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ESPECIALIDADES = [
  "Harmonização / Estética facial",
  "Dermatologia",
  "Cirurgia Plástica",
  "Odontologia Estética",
  "Nutrologia",
  "Outros",
];

const FATURAMENTOS = [
  "Até R$ 30 mil",
  "R$ 30 mil — R$ 100 mil",
  "R$ 100 mil — R$ 300 mil",
  "Acima de R$ 300 mil",
];

const DESAFIOS = [
  "Baixa conversão de leads",
  "Poucos leads qualificados",
  "Equipe despreparada",
  "No-show alto",
  "Falta de indicadores",
];

const BARS = [
  { label: "Tempo de resposta", seq: ["78%", "32%", "58%", "24%", "78%"] },
  { label: "Follow-up ativo", seq: ["35%", "72%", "48%", "86%", "35%"] },
  { label: "Confirmação de agenda", seq: ["60%", "88%", "42%", "70%", "60%"] },
];

const ALERTS = [
  { icon: PhoneMissed, text: "Lead sem resposta há 2h 14min" },
  { icon: CalendarX, text: "3 agendamentos sem confirmação" },
  { icon: TrendingUp, text: "Follow-up recuperou R$ 4.800 em vendas" },
  { icon: AlertTriangle, text: "No-show acima da média hoje" },
];

const initialForm = {
  nome: "",
  clinica: "",
  whatsapp: "",
  especialidade: "",
  faturamento: "",
  desafio: "",
  consentimento: false,
};

const inputClass =
  "w-full bg-ink/70 border border-gold/20 rounded-xl px-4 py-3 text-bone placeholder:text-bone/30 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold/50 transition-colors text-sm";

const labelClass = "block text-[11px] uppercase tracking-widest text-bone/60 mb-1.5";
const errClass = "mt-1 text-xs text-gold";

function RadarDashboard() {
  const reduce = useReducedMotion();
  const [ai, setAi] = useState(0);

  useEffect(() => {
    if (reduce) return;
    const t = setInterval(() => setAi((a) => (a + 1) % ALERTS.length), 2600);
    return () => clearInterval(t);
  }, [reduce]);

  const Alert = ALERTS[ai];
  const C = 2 * Math.PI * 38;

  return (
    <div
      data-testid="diagnostic-dashboard"
      className="relative bg-graphite/60 border border-gold/25 rounded-3xl p-6 sm:p-8 overflow-hidden"
    >
      {!reduce && (
        <div className="animate-scan-y absolute left-0 right-0 h-20 bg-gradient-to-b from-transparent via-gold/10 to-transparent pointer-events-none" />
      )}

      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-2.5 text-xs uppercase tracking-[0.24em] font-semibold text-gold">
          <span className="relative flex w-2.5 h-2.5">
            <span className="absolute inline-flex w-full h-full rounded-full bg-gold opacity-60 animate-ping" />
            <span className="relative inline-flex w-2.5 h-2.5 rounded-full bg-gold" />
          </span>
          Radar comercial
        </span>
        <Radar size={18} className="text-gold/60" />
      </div>

      <div className="mt-7 flex items-center gap-5">
        <div className="relative w-24 h-24 shrink-0">
          <svg viewBox="0 0 96 96" className="w-24 h-24 -rotate-90">
            <circle cx="48" cy="48" r="38" stroke="rgba(245,242,235,0.08)" strokeWidth="7" fill="none" />
            <motion.circle
              cx="48" cy="48" r="38"
              stroke="#E9B83F" strokeWidth="7" fill="none" strokeLinecap="round"
              strokeDasharray={C}
              animate={reduce ? {} : { strokeDashoffset: [C, C * 0.28, C * 0.6, C * 0.18, C] }}
              transition={{ duration: 11, repeat: Infinity, ease: "easeInOut" }}
            />
          </svg>
          <span className="absolute inset-0 flex items-center justify-center font-display font-extrabold text-gold text-sm tracking-tight">
            AO VIVO
          </span>
        </div>
        <div>
          <p className="font-display font-bold text-bone text-lg leading-snug">
            É assim que lemos a operação da sua clínica
          </p>
          <p className="text-sm text-bone/55 mt-1.5 leading-relaxed">
            Resposta, agenda, follow-up e conversão — medidos em tempo real.
          </p>
        </div>
      </div>

      <div className="mt-7 space-y-4">
        {BARS.map((b, i) => (
          <div key={b.label}>
            <div className="flex justify-between text-[11px] uppercase tracking-widest text-bone/50 mb-1.5">
              <span>{b.label}</span>
              <span className="w-2 h-2 rounded-full bg-gold/70 animate-pulse" style={{ animationDelay: `${i * 0.4}s` }} />
            </div>
            <div className="h-2 bg-ink rounded-full overflow-hidden">
              <motion.div
                className="h-full rounded-full bg-gradient-to-r from-gold-deep to-gold"
                style={{ width: b.seq[0] }}
                animate={reduce ? {} : { width: b.seq }}
                transition={{ duration: 12, repeat: Infinity, ease: "easeInOut", delay: i * 0.8 }}
              />
            </div>
          </div>
        ))}
      </div>

      <div className="mt-7 bg-ink/70 border border-gold/15 rounded-2xl px-4 py-3.5 min-h-[52px] flex items-center">
        <AnimatePresence mode="wait">
          <motion.div
            key={ai}
            initial={reduce ? false : { opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduce ? undefined : { opacity: 0, y: -10 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="flex items-center gap-3"
            data-testid="diagnostic-radar-alert"
          >
            <span className="w-8 h-8 rounded-lg bg-gold/15 border border-gold/30 flex items-center justify-center shrink-0">
              <Alert.icon size={15} className="text-gold" />
            </span>
            <p className="text-sm text-bone/80">{Alert.text}</p>
          </motion.div>
        </AnimatePresence>
      </div>
      <p className="mt-4 text-[10px] text-bone/30 uppercase tracking-widest">Painel ilustrativo do acompanhamento Pulse</p>
    </div>
  );
}

export default function DiagnosticForm() {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (form.nome.trim().length < 2) er.nome = "Informe seu nome";
    if (form.clinica.trim().length < 2) er.clinica = "Informe a clínica";
    if (form.whatsapp.replace(/\D/g, "").length < 8) er.whatsapp = "WhatsApp inválido";
    if (!form.especialidade) er.especialidade = "Selecione";
    if (!form.faturamento) er.faturamento = "Selecione";
    if (!form.desafio) er.desafio = "Selecione";
    if (!form.consentimento) er.consentimento = "É necessário aceitar o tratamento dos dados";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Revise os campos destacados antes de enviar.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/diagnostics`, form);
      toast.success("Diagnóstico solicitado! Nossa equipe entrará em contato.");
      const msg = `Olá! Sou ${form.nome}, da clínica ${form.clinica}. Acabei de solicitar um diagnóstico comercial pelo site da Pulse Saúde e quero falar com um especialista.`;
      window.open(waLink(msg), "_blank", "noopener,noreferrer");
      setForm(initialForm);
    } catch (err) {
      toast.error("Não foi possível enviar agora. Tente novamente em instantes.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="diagnostico" data-testid="diagnostic-section" className="bg-ink py-20 lg:py-28 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-[360px] h-[360px] lg:w-[600px] lg:h-[600px] rounded-full bg-gold/7 blur-[70px] lg:blur-[150px] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 lg:px-10 relative">
        <div className="max-w-3xl">
          <Reveal>
            <SectionTag>Diagnóstico comercial</SectionTag>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-5 font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-bone leading-[1.12]" data-testid="diagnostic-title">
              Descubra onde sua clínica está{" "}
              <span className="text-gold">perdendo oportunidades.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-5 text-base sm:text-lg text-bone/60 leading-relaxed">
              Responda em menos de um minuto e receba a leitura dos gargalos da sua operação.
            </p>
          </Reveal>
        </div>

        <div className="mt-12 grid lg:grid-cols-2 gap-8 items-start">
          <Reveal delay={0.15}>
            <RadarDashboard />
          </Reveal>

          <Reveal delay={0.2}>
            <form
              onSubmit={submit}
              data-testid="diagnostic-form"
              className="bg-graphite/60 border border-gold/25 rounded-3xl p-6 sm:p-8"
              noValidate
            >
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="diag-nome" className={labelClass}>Nome completo</label>
                  <input id="diag-nome" data-testid="diagnostic-input-nome" className={inputClass} placeholder="Seu nome" value={form.nome} onChange={set("nome")} />
                  {errors.nome && <p data-testid="diagnostic-error-nome" className={errClass}>{errors.nome}</p>}
                </div>
                <div>
                  <label htmlFor="diag-clinica" className={labelClass}>Clínica / Consultório</label>
                  <input id="diag-clinica" data-testid="diagnostic-input-clinica" className={inputClass} placeholder="Nome da clínica" value={form.clinica} onChange={set("clinica")} />
                  {errors.clinica && <p data-testid="diagnostic-error-clinica" className={errClass}>{errors.clinica}</p>}
                </div>
                <div>
                  <label htmlFor="diag-whatsapp" className={labelClass}>WhatsApp</label>
                  <input id="diag-whatsapp" data-testid="diagnostic-input-whatsapp" type="tel" className={inputClass} placeholder="(11) 99999-9999" value={form.whatsapp} onChange={set("whatsapp")} />
                  {errors.whatsapp && <p data-testid="diagnostic-error-whatsapp" className={errClass}>{errors.whatsapp}</p>}
                </div>
                <div>
                  <label htmlFor="diag-especialidade" className={labelClass}>Especialidade</label>
                  <select id="diag-especialidade" data-testid="diagnostic-select-especialidade" className={inputClass} value={form.especialidade} onChange={set("especialidade")}>
                    <option value="">Selecione</option>
                    {ESPECIALIDADES.map((e) => <option key={e} value={e}>{e}</option>)}
                  </select>
                  {errors.especialidade && <p data-testid="diagnostic-error-especialidade" className={errClass}>{errors.especialidade}</p>}
                </div>
                <div>
                  <label htmlFor="diag-faturamento" className={labelClass}>Faturamento mensal</label>
                  <select id="diag-faturamento" data-testid="diagnostic-select-faturamento" className={inputClass} value={form.faturamento} onChange={set("faturamento")}>
                    <option value="">Selecione</option>
                    {FATURAMENTOS.map((f) => <option key={f} value={f}>{f}</option>)}
                  </select>
                  {errors.faturamento && <p data-testid="diagnostic-error-faturamento" className={errClass}>{errors.faturamento}</p>}
                </div>
                <div>
                  <label htmlFor="diag-desafio" className={labelClass}>Principal desafio</label>
                  <select id="diag-desafio" data-testid="diagnostic-select-desafio" className={inputClass} value={form.desafio} onChange={set("desafio")}>
                    <option value="">Selecione</option>
                    {DESAFIOS.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                  {errors.desafio && <p data-testid="diagnostic-error-desafio" className={errClass}>{errors.desafio}</p>}
                </div>
              </div>

              <label className="mt-5 flex items-start gap-3 cursor-pointer select-none" data-testid="diagnostic-consent-wrapper">
                <input
                  type="checkbox"
                  data-testid="diagnostic-checkbox-consent"
                  checked={form.consentimento}
                  onChange={set("consentimento")}
                  className="mt-0.5 w-4 h-4 accent-[#E9B83F]"
                />
                <span className="text-xs text-bone/55 leading-relaxed">
                  Autorizo a Pulse Saúde a tratar meus dados para contato e elaboração do
                  diagnóstico comercial, conforme a LGPD (Lei nº 13.709/2018).
                </span>
              </label>
              {errors.consentimento && <p data-testid="diagnostic-error-consentimento" className={`${errClass} mt-1.5`}>{errors.consentimento}</p>}

              <button
                type="submit"
                data-testid="diagnostic-submit-button"
                disabled={sending}
                className="group mt-6 w-full inline-flex items-center justify-center gap-3 bg-gold text-ink font-display font-extrabold text-base px-8 py-4 rounded-full hover:bg-bone transition-colors duration-300 disabled:opacity-60"
              >
                {sending ? (
                  <>
                    <Loader2 size={19} className="animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    Quero um diagnóstico da minha clínica
                    <Send size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </>
                )}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
